import { html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import Styles from './vm-icon-next.scss';

const iconModules = import.meta.glob('./assets/ic-*.svg', {
    query: '?raw',
    import: 'default',
});

export const iconNames = [
    'alert-triangle',
    'arrow-narrow-right',
    'arrow-narrow-up-right',
    'arrow-up',
    'attachment-02',
    'award-03',
    'calendar',
    'check-circle',
    'check-circle-solid',
    'chevron-down',
    'linkedin',
    'edit-05',
    'external-link',
    'file-06',
    'image-03',
    'link-01',
    'loading',
    'message-square-02',
    'plus',
    'play',
    'save',
    'search',
    'sparkle',
    'star',
    'stars-01',
    'mail-01',
    'share-01',
    'thumbs-up',
    'thumbs-up-solid',
    'stop',
    'x-circle',
    'x-close',
    'x-twitter',
] as const;
export type IconName = (typeof iconNames)[number];

export type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const cache = new Map<string, string>();

@customElement('vm-icon-next')
export class VmIconNext extends LitElement {
    static override styles = Styles;

    @property()
    name!: IconName;

    @property({ reflect: true })
    size: IconSize = 'md';

    @state()
    private svg = '';

    override willUpdate(changed: Map<string, unknown>) {
        if (changed.has('name') || changed.has('size')) {
            this.loadIcon();
        }
    }

    private async loadIcon() {
        const { name, size } = this;

        if (!name) {
            this.svg = '';
            return;
        }

        const useSmall = size === 'xs' || size === 'sm';
        const cacheKey = `${name}-${useSmall ? 'sm' : 'md'}`;
        const cached = cache.get(cacheKey);

        if (cached) {
            this.svg = cached;
            return;
        }

        const loader = useSmall
            ? (iconModules[`./assets/ic-${name}-16.svg`] ?? iconModules[`./assets/ic-${name}.svg`])
            : iconModules[`./assets/ic-${name}.svg`];
        if (!loader) {
            this.svg = '';
            return;
        }

        const svg = (await loader()) as string;
        cache.set(cacheKey, svg);

        // Guard against name changing while awaiting
        if (this.name === name && this.size === size) {
            this.svg = svg;
        }
    }

    override render() {
        return this.svg ? html`${unsafeSVG(this.svg)}` : nothing;
    }
}
